import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithApprovalResponses } from 'ai';
import type { UIMessage } from 'ai';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { useOptibotStore } from '../stores/optibot-store';
import { getChatApiUrl, CHAT_REQUEST_TIMEOUT_MS } from '../utils/constants';
import { ChatError, classifyOptiBotError, getErrorI18nKey } from '../utils/error-classification';
import { getViewContext } from '../utils/view-context';

const CHAT_API_URL = getChatApiUrl();

/**
 * Module-level pending image IDs for the next chat request.
 * Written by OptiBotPanel before sendMessage, read by the transport body callback,
 * then cleared by the transport fetch callback after the request fires.
 */
let _pendingImageIds: string[] = [];

/** Set image IDs to include in the next chat request body. */
export function setActiveImageIds(ids: string[]) {
  _pendingImageIds = ids;
}

/** sessionStorage key scoped by teamId so switching teams starts fresh */
function getSessionKey(teamId: string) {
  return `optibot-session-${teamId}`;
}

function getThreadIdKey(teamId: string) {
  return `optibot-threadId-${teamId}`;
}

/** Read cached messages from sessionStorage */
function readCachedMessages(teamId: string): UIMessage[] | undefined {
  try {
    const raw = sessionStorage.getItem(getSessionKey(teamId));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as UIMessage[];
    if (!Array.isArray(parsed) || parsed.length === 0) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

/** Persist messages to sessionStorage (strips file parts to avoid 5MB limit bloat) */
function writeCachedMessages(teamId: string, messages: UIMessage[]) {
  try {
    const stripped = messages.map((msg) => ({
      ...msg,
      parts: msg.parts.filter((p) => p.type !== 'file'),
    }));
    sessionStorage.setItem(getSessionKey(teamId), JSON.stringify(stripped));
  } catch {
    // sessionStorage full or unavailable — silently ignore
  }
}

export function useOptiBot() {
  const { t } = useTranslation();
  const teamId = useTeamStore((s) => s.activeTeamId);

  const [input, setInput] = useState('');
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  // Restore activeThreadId from sessionStorage when teamId becomes available
  useEffect(() => {
    if (!teamId) return;
    const stored = sessionStorage.getItem(getThreadIdKey(teamId));
    if (stored) setActiveThreadId(stored);
  }, [teamId]);

  /** Maps assistant messageId → X-Request-Id from the backend response header. */
  const requestIdMapRef = useRef(new Map<string, string>());

  /** Maps messageId → timestamp (first-seen time). */
  const messageTimestampRef = useRef(new Map<string, Date>());

  // Read token fresh from the store at request time (not from a stale closure).
  // This avoids 401s when Firebase refreshes the token after the transport was created.
  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: CHAT_API_URL,
        headers: () => ({ Authorization: `Bearer ${useAuthStore.getState().token}` }),
        body: () => ({
          teamId: useTeamStore.getState().activeTeamId,
          viewContext: getViewContext(),
          ...(_pendingImageIds.length > 0 ? { uploadedImageIds: [..._pendingImageIds] } : {}),
        }),
        fetch: async (url, init) => {
          // Clear pending image IDs after the request is sent
          _pendingImageIds = [];

          // Merge SDK abort signal with a timeout signal
          const timeoutSignal = AbortSignal.timeout(CHAT_REQUEST_TIMEOUT_MS);
          const combinedSignal = init?.signal
            ? AbortSignal.any([init.signal, timeoutSignal])
            : timeoutSignal;

          const response = await fetch(url, { ...init, signal: combinedSignal });

          if (!response.ok) {
            throw new ChatError(`Chat request failed (${response.status})`, response.status);
          }

          const requestId = response.headers.get('X-Request-Id');
          if (requestId) {
            // Store temporarily — will be mapped to the assistant message ID in onFinish
            requestIdMapRef.current.set('__latest__', requestId);
          }
          return response;
        },
      }),
    [],
  );

  // Restore cached messages from sessionStorage (team-scoped)
  const cachedMessages = useMemo(() => readCachedMessages(teamId), [teamId]);

  const { messages, sendMessage, setMessages, status, error, stop, addToolApprovalResponse } =
    useChat({
      transport,
      messages: cachedMessages,
      sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
      onFinish: ({ messages: updatedMessages }) => {
        writeCachedMessages(teamId, updatedMessages);
        // Map the captured X-Request-Id to the last assistant message
        const latestRequestId = requestIdMapRef.current.get('__latest__');
        if (latestRequestId) {
          const lastAssistant = updatedMessages.filter((m) => m.role === 'assistant').pop();
          if (lastAssistant) {
            requestIdMapRef.current.set(lastAssistant.id, latestRequestId);
          }
          requestIdMapRef.current.delete('__latest__');
        }
      },
      onError: (error) => {
        const errorType = classifyOptiBotError(error);
        toast.error(t(getErrorI18nKey(errorType)));
      },
    });

  const isLoading = status === 'submitted' || status === 'streaming';

  // Track first-seen timestamps for each message
  useEffect(() => {
    const tsMap = messageTimestampRef.current;
    for (const msg of messages) {
      if (!tsMap.has(msg.id)) {
        // createdAt is set on history-loaded messages (not in UIMessage type)
        const raw = 'createdAt' in msg ? msg.createdAt : undefined;
        const created = raw ? new Date(raw as string | Date) : undefined;
        tsMap.set(msg.id, created ?? new Date());
      }
    }
  }, [messages]);

  /** Get timestamp for a message by ID */
  const getMessageTimestamp = useCallback((messageId: string): Date | undefined => {
    return messageTimestampRef.current.get(messageId);
  }, []);

  // Poll for live agent replies while the chat panel is open.
  // When not escalated the backend returns { escalated: false } immediately (no-op).
  // When escalated it returns the next unprocessed agent reply, which is injected
  // directly into the message list so it appears without a page refresh.
  const isOpen = useOptibotStore((s) => s.isOpen);
  useEffect(() => {
    if (!isOpen || !teamId) return;

    const poll = async () => {
      try {
        const res = await globalThis.fetch(`${CHAT_API_URL}/agent-replies?teamId=${teamId}`, {
          headers: { Authorization: `Bearer ${useAuthStore.getState().token}` },
        });
        if (!res.ok) return;

        const data = (await res.json()) as {
          escalated: boolean;
          type?: 'reply' | 'closed' | 'timeout' | 'none';
          message?: string;
        };

        if (!data.escalated || data.type === 'none' || !data.message) return;

        const agentMsg: UIMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          parts: [{ type: 'text', text: data.message }],
          metadata: { isAgentReply: true },
        };
        setMessages((prev) => {
          const updated = [...prev, agentMsg];
          writeCachedMessages(teamId, updated);
          return updated;
        });
      } catch {
        // network error — silently skip, next tick will retry
      }
    };

    const interval = setInterval(poll, 7_000);
    return () => clearInterval(interval);
  }, [isOpen, teamId, setMessages]);

  /** Stop streaming and clean up incomplete tool invocations from messages.
   *  Without cleanup, the next sendMessage would include incomplete tool_use blocks
   *  (no matching tool_result), causing the backend to reject the request silently.
   *  Replaces removed tool parts with a "stopped" text so the message isn't orphaned. */
  const handleStop = useCallback(() => {
    stop();
    const stoppedPart = { type: 'text' as const, text: '{{STOPPED}}' };
    setMessages((prev) => {
      const lastIdx = prev.length - 1;
      if (lastIdx < 0) return prev;
      const lastMsg = prev[lastIdx];

      // Case 1: Stop before response started — last message is user, add assistant placeholder
      if (lastMsg.role === 'user') {
        return [
          ...prev,
          { id: crypto.randomUUID(), role: 'assistant' as const, parts: [stoppedPart] },
        ];
      }

      // Case 2: Stop during streaming — clean incomplete tool parts from assistant message
      if (lastMsg.role === 'assistant') {
        const cleanedParts = lastMsg.parts.filter(
          (part) =>
            !(
              'state' in part &&
              (part.state === 'input-streaming' || part.state === 'input-available')
            ),
        );
        if (cleanedParts.length === lastMsg.parts.length) return prev;
        return [...prev.slice(0, lastIdx), { ...lastMsg, parts: [...cleanedParts, stoppedPart] }];
      }

      return prev;
    });
  }, [stop, setMessages]);

  /** Clear session and reset chat to empty state */
  const clearChat = useCallback(() => {
    sessionStorage.removeItem(getSessionKey(teamId));
    sessionStorage.removeItem(getThreadIdKey(teamId));
    requestIdMapRef.current.clear();
    messageTimestampRef.current.clear();
    setActiveThreadId(null);
    setMessages([]);
  }, [teamId, setMessages]);

  /** Load a previous conversation by threadId from the backend */
  const loadConversation = useCallback(
    async (threadId: string) => {
      setIsLoadingHistory(true);
      try {
        const response = await globalThis.fetch(`${CHAT_API_URL}/history/${threadId}`, {
          headers: { Authorization: `Bearer ${useAuthStore.getState().token}` },
        });
        if (!response.ok) {
          toast.error(t('optibot.error'));
          return;
        }
        const data = (await response.json()) as {
          role: string;
          content: string;
          createdAt: string;
          isAgentReply?: boolean;
        }[];
        // Filter out HubSpot system/metadata messages and non-chat roles
        const filtered = data.filter((msg) => {
          if (msg.role !== 'user' && msg.role !== 'assistant') return false;
          const text = msg.content.trim();
          if (!text) return false;
          if (/^First Name:/i.test(text)) return false;
          if (/OptiBot conversation started by/i.test(text)) return false;
          if (/^\[Escalation Summary\]/i.test(text)) return false;
          if (/^\[System\]/i.test(text)) return false;
          return true;
        });
        // Backend returns newest-first — reverse to chronological order
        filtered.reverse();
        const uiMessages: UIMessage[] = filtered.map((msg) => ({
          id: crypto.randomUUID(),
          role: msg.role as 'user' | 'assistant',
          parts: [{ type: 'text' as const, text: msg.content }],
          createdAt: new Date(msg.createdAt),
          ...(msg.isAgentReply ? { metadata: { isAgentReply: true } } : {}),
        }));
        requestIdMapRef.current.clear();
        setActiveThreadId(threadId);
        sessionStorage.setItem(getThreadIdKey(teamId), threadId);
        setMessages(uiMessages);
        writeCachedMessages(teamId, uiMessages);
      } catch {
        toast.error(t('optibot.error'));
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [teamId, setMessages, t],
  );

  const handleApprove = useCallback(
    (toolCallId: string) => {
      addToolApprovalResponse({ id: toolCallId, approved: true });
    },
    [addToolApprovalResponse],
  );

  const handleDeny = useCallback(
    (toolCallId: string) => {
      addToolApprovalResponse({ id: toolCallId, approved: false });
    },
    [addToolApprovalResponse],
  );

  /** Fire-and-forget feedback submission to the backend. */
  const handleFeedback = useCallback(async (messageId: string, score: number, comment?: string) => {
    const requestId = requestIdMapRef.current.get(messageId);
    if (!requestId) return;

    try {
      const feedbackUrl = `${CHAT_API_URL}/feedback`;
      await globalThis.fetch(feedbackUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${useAuthStore.getState().token}`,
        },
        body: JSON.stringify({
          messageId: requestId,
          score,
          teamId: useTeamStore.getState().activeTeamId,
          ...(comment ? { comment } : {}),
        }),
      });
    } catch {
      // Fire-and-forget — silently ignore errors
    }
  }, []);

  return {
    messages,
    sendMessage,
    isLoading,
    isLoadingHistory,
    activeThreadId,
    error,
    status,
    stop: handleStop,
    handleApprove,
    handleDeny,
    handleFeedback,
    clearChat,
    loadConversation,
    getMessageTimestamp,
    input,
    setInput,
  };
}
